import type { LoginResult } from '@/pages/login/model';
import type { ServerResult } from '@south/request';
import { request } from '@/utils/request';

enum API {
  URL = '/system/user',
}

/**
 * 获取分页数据
 * @param data - 请求数据
 */
export function getUserPage(data: Partial<BaseFormData> & PaginationData) {
  return request.get<PageServerResult<BaseFormData[]>>(`${API.URL}/page`, { params: data });
}

/**
 * 根据ID获取数据
 * @param id - ID
 */
export function getUserById(id: string) {
  return request.get<BaseFormData>(`${API.URL}/detail?id=${id}`);
}

/**
 * 新增数据
 * @param data - 请求数据
 */
export function createUser(data: BaseFormData) {
  return request.post(`${API.URL}/create`, data);
}

/**
 * 修改数据
 * @param id - 修改id值
 * @param data - 请求数据
 */
export function updateUser(id: string, data: BaseFormData) {
  return request.put(`${API.URL}/update/${id}`, data);
}

/**
 * 删除
 * @param id - 删除id值
 */
export function deleteUser(id: string) {
  return request.delete(`${API.URL}/${id}`);
}

/**
 * 批量删除
 * @param data - 请求数据
 */
export function batchDeleteUser(data: BaseFormData) {
  return request.post(`${API.URL}/batchDelete`, data);
}

/** 用户权限接口返回结构（后端返回 permissions / menus） */
export interface UserPermissionResult {
  permissions: string[];
  menus: string[];
}

/**
 * 获取用户当前权限（包含角色权限合并后的结果）
 * @param data - 搜索数据（id 或 userId）
 */
export function getUserPermission(data: object): Promise<ServerResult<UserPermissionResult>> {
  return request.get<UserPermissionResult>(`${API.URL}/authorize`, {
    params: data,
  });
}

/**
 * 保存用户权限
 * @param data - 权限数据
 */
export function saveUserPermission(data: object) {
  return request.put(`${API.URL}/authorize/save`, data);
}

/**
 * 获取所有可配置的页面权限列表
 */
export function getAllPagePermissions(): Promise<ServerResult<Array<{ page: string; pageName: string; actions: Array<{ action: string; actionName: string; permission: string }> }>>> {
  return request.get<Array<{ page: string; pageName: string; actions: Array<{ action: string; actionName: string; permission: string }> }>>(`${API.URL}/permissions/list`);
}

/**
 * 获取当前登录用户的权限（用于刷新缓存）
 * @param _data - 请求数据（目前未使用，保留兼容）
 */
export async function getUserRefreshPermissions(_data: object): Promise<{ code: number; data: LoginResult }> {
  // 1. 通过 /auth/profile 获取当前登录用户信息（依赖后端 JwtStrategy 从数据库加载用户）
  // 注意：/auth/profile 直接返回用户对象，不包装在 ServerResult 中
  const userProfileResponse = await request.get<{ id: string; email: string; name: string | null; role: string }>(
    '/auth/profile',
  );
  
  // request.get 返回 ServerResult<T>，但 /auth/profile 可能直接返回用户对象
  // 需要兼容两种情况：ServerResult 包装的或直接返回的
  const userProfile = (userProfileResponse as any)?.data || userProfileResponse;
  console.log('获取用户信息:', userProfile);

  // 2. 从 /system/user/my-permissions 获取当前用户的权限
  // 这个接口不需要特殊权限，允许用户获取自己的权限
  const userAuthResponse = await request.get<{ permissions: string[] }>(
    `/system/user/my-permissions`,
  );

  console.log('获取权限响应:', userAuthResponse);

  // request.get 返回 ServerResult<T>，需要访问 .data 属性
  const permissions = (userAuthResponse as any)?.data?.permissions || ['/dashboard'];
  
  console.log('解析后的权限:', permissions);
  console.log('权限数量:', permissions.length);
  console.log('是否包含 /authority/user/index:', permissions.includes('/authority/user/index'));

  // 3. 组装成 LoginResult 返回给前端
  const result: LoginResult = {
    token: '', // 刷新权限时不改变 token
    user: {
      id: userProfile.id,
      username: userProfile.name || userProfile.email,
      phone: '',
      email: userProfile.email,
      roles: [userProfile.role],
    },
    permissions,
    roles: [{ id: userProfile.role }],
  };

  return {
    code: 200,
    data: result,
  };
}
